import { useState, useEffect } from "react";
import {
  Form,
  Input,
  Upload,
  Button,
  Card,
  Space,
  message,
  Image,
  Spin,
} from "antd";
import {
  UploadOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateAdvertMutation } from "./advert.service";

const { TextArea } = Input;

const AdvertCreatePage = () => {
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const [fileList, setFileList] = useState([]);
  const [imageChanged, setImageChanged] = useState(false);

  const [
    requestCreateAdvert,
    { isSuccess: isCreateSuccess, isLoading: isCreateLoading },
  ] = useCreateAdvertMutation();

  useEffect(() => {
    if (isCreateSuccess) {
      form.resetFields();
      navigate("/advert");
    }
  }, [isCreateSuccess]);

  // Upload configuration
  const uploadProps = {
    name: "image",
    fileList: fileList,
    maxCount: 1,
    accept: "image/*",
    showUploadList: false,
    beforeUpload: (file) => {
      // Validate file type
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return false;
      }

      // Validate file size (50MB)
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        message.error("Image must be smaller than 50MB!");
        return false;
      }

      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      // Filter out files that failed validation
      const validFiles = newFileList.filter((file) => {
        if (file.originFileObj) {
          const isImage = file.originFileObj.type.startsWith("image/");
          const isValidSize = file.originFileObj.size / 1024 / 1024 < 50;
          return isImage && isValidSize;
        }
        return true; // Keep existing valid files
      });

      setFileList(validFiles);
      setImageChanged(validFiles.length > 0 && validFiles[0].originFileObj); // New file selected
    },
    onRemove: () => {
      setFileList([]);
      setImageChanged(true); // Image removed
    },
  };

  // Handle form submission
  const onFinish = async (values) => {
    const formData = new FormData();

    const titleToSend = values.title || "";
    if (titleToSend.trim()) {
      formData.append("title", titleToSend);
    }
    formData.append("websiteUrl", values.websiteUrl);

    if (imageChanged && fileList.length > 0 && fileList[0].originFileObj) {
      formData.append("image", fileList[0].originFileObj);
    }

    await requestCreateAdvert(formData);
  };

  // Upload button
  const uploadButton = (
    <Button
      icon={<UploadOutlined />}
      size="large"
      style={{ width: "100%", height: "48px" }}
    >
      Choose New Image
    </Button>
  );

  return (
    <Card
      title="Advert Create"
      style={{ fontSize: "20px" }}
      bordered={false}
      extra={
        <Button
          type="default"
          style={{ marginRight: 8 }}
          onClick={() => navigate("/advert")}
        >
          Discard
        </Button>
      }
    >
      <Spin spinning={isCreateLoading}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label={
              <Space>
                <UploadOutlined />
                <span>Update Image (Optional)</span>
              </Space>
            }
            extra="Leave empty to keep current image. Supported formats: JPEG, PNG, GIF, WebP, SVG. Maximum size: 50MB"
          >
            {fileList.length === 0 || !fileList[0].originFileObj ? (
              <Upload {...uploadProps}>{uploadButton}</Upload>
            ) : (
              <div>
                <div
                  style={{
                    marginBottom: 12,
                    color: "#52c41a",
                    fontWeight: "bold",
                  }}
                >
                  ✓ New image selected: {fileList[0].name}
                </div>
                <Image
                  width={200}
                  height={150}
                  src={URL.createObjectURL(fileList[0].originFileObj)}
                  alt="New advert image"
                  style={{
                    objectFit: "cover",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                  }}
                  placeholder={
                    <div
                      style={{
                        width: 200,
                        height: 150,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f5f5f5",
                      }}
                    >
                      Loading...
                    </div>
                  }
                />
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setFileList([]);
                      setImageChanged(false);
                    }}
                    danger
                    icon={<DeleteOutlined />}
                  >
                    Remove New Image
                  </Button>
                </div>
              </div>
            )}
          </Form.Item>

          {/* Title Section */}
          <Form.Item
            label={
              <Space>
                <span>Advert Title</span>
                <span style={{ color: "#ff4d4f" }}>*</span>
              </Space>
            }
            name={"title"}
            rules={[
              { required: true, message: "English title is required" },
              { max: 200, message: "Title cannot exceed 200 characters" },
            ]}
            required
          >
            <TextArea
              rows={3}
              maxLength={200}
              showCount
              style={{ resize: "none" }}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>Advert WebsiteUrl</span>
                <span style={{ color: "#ff4d4f" }}>*</span>
              </Space>
            }
            name={"websiteUrl"}
            rules={[{ required: true, message: "WebsiteUrl is required" }]}
            required
          >
            <Input style={{ resize: "none" }} />
          </Form.Item>

          {/* Submit Buttons */}
          <Form.Item style={{ marginTop: 32 }}>
            <Space style={{ width: "100%" }} direction="vertical">
              <Button
                type="primary"
                htmlType="submit"
                loading={isCreateLoading}
                icon={<SaveOutlined />}
                size="large"
                block
                style={{ height: "48px", fontSize: "16px" }}
              >
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default AdvertCreatePage;
